// plugins/i18n.ts (SEM .client!)
import { createI18n } from "vue-i18n";
import { i18nReady } from "@/composables/i18nReady";
import { fetchI18nMessages } from "@/composables/useLocales";

// Importar mensagens locais APENAS como fallback se fetch falhar
import pt from "~/i18n/locales/pt.json";
import en from "~/i18n/locales/en.json";
import es from "~/i18n/locales/es.json";

const localeFallbacks: Record<string, any> = {
  pt: pt || {},
  en: en || {},
  es: es || {},
};

export default defineNuxtPlugin(async (nuxtApp) => {
  console.log("[i18n plugin] Iniciando...");

  // Suportar locales: pt, en, es
  const supportedLocales = ["pt", "en", "es"];

  // Determinar locale inicial (cookie > navegador > PT)
  let defaultLocale = "pt";

  // 1. Tentar ler do cookie
  const localeCookie = useCookie<string>("i18n_locale");
  if (localeCookie.value && supportedLocales.includes(localeCookie.value)) {
    defaultLocale = localeCookie.value;
    console.log("[i18n plugin] Locale do cookie:", defaultLocale);
  }
  // 2. Tentar detectar do navegador
  else if (process.client) {
    const browserLocale = navigator.language?.split("-")[0] || "";
    if (supportedLocales.includes(browserLocale)) {
      defaultLocale = browserLocale;
      localeCookie.value = defaultLocale;
      console.log("[i18n plugin] Locale do navegador:", defaultLocale);
    }
  }

  console.log("[i18n plugin] Locale inicial:", defaultLocale);

  // Mensagens iniciais VAZIAS (fetch remoto é source of truth)
  const messages: Record<string, any> = {
    pt: {},
    en: {},
    es: {},
  };

  const i18n = createI18n({
    legacy: false,
    locale: defaultLocale,
    fallbackLocale: defaultLocale,
    messages,
    missingWarn: false,
    fallbackWarn: false,
  });

  nuxtApp.vueApp.use(i18n);

  // Aguardar fetch remoto com timeout. Só depois sinalizamos i18nReady.
  // Isto garante que o loader permanece até o i18n estar pronto.
  const fetchTimeoutMs = 3000;

  try {
    console.log("[i18n plugin] Fetching messages para:", defaultLocale);
    const fetchPromise = fetchI18nMessages(defaultLocale);
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error("i18n fetch timeout")), fetchTimeoutMs)
    );

    const fetched = await Promise.race([fetchPromise, timeoutPromise]);
    console.log(
      "[i18n plugin] Fetch completo, chaves:",
      Object.keys(fetched || {}).length
    );

    if (
      fetched &&
      typeof fetched === "object" &&
      Object.keys(fetched).length > 0
    ) {
      const merged = Object.assign({}, messages[defaultLocale] || {}, fetched);
      i18n.global.setLocaleMessage(defaultLocale, merged);
      // Atualizar locale do i18n com o detectado
      i18n.global.locale.value = defaultLocale;
      console.log("[i18n plugin] i18n pronto com locale:", defaultLocale);
    } else {
      console.warn(
        "i18n: fetched messages invalid or empty, keeping local messages"
      );
    }
  } catch (err: any) {
    // Timeout ou erro: usar fallback local
    console.warn(
      "Erro/timeout ao obter mensagens i18n remotas, usando fallback local:",
      err?.message || err
    );
    const fallback = localeFallbacks[defaultLocale] || {};
    i18n.global.setLocaleMessage(defaultLocale, fallback);
  } finally {
    // Agora sinalizamos que i18n está pronto (com mensagens remotas ou locais)
    console.log("[i18n plugin] i18nReady = true");
    i18nReady.value = true;
  }
});
